import { useCallback, useEffect, useRef, useState } from 'react'
import { checkAccess, requestAccess } from '../service/access'
import { useParams } from 'react-router-dom'
import { SERVICE_DOMAIN } from '../constants'
import { isServiceName, serviceURL } from '../utilities/serviceName'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Modal from './Modal'
import useAuth from '../models/useAuth'
import useAccessRequestWatcher from '../hooks/useAccessRequestWatcher'
import Alert from '@mui/material/Alert'
import { LuBan, LuCheck } from 'react-icons/lu'

type Status = 'requesting' | 'sent' | 'error' | 'rejected'

export default function AccessRequest() {
  const { serviceName: routeParam } = useParams<{ serviceName: string }>()
  // Anybody can make this link, and the page POSTs on arrival. A name that is not a
  // DNS label is not a service: no fetch and no redirect (app#377).
  const validName = isServiceName(routeParam)
  const serviceName = validName ? routeParam : undefined
  const email = useAuth(state => state.user?.email ?? '')

  const [serviceTitle, setServiceTitle] = useState(serviceName ?? '')
  const [status, setStatus] = useState<Status>('requesting')
  const [errorMessage, setErrorMessage] = useState('')

  const redirectToService = useCallback(() => {
    const url = serviceName ? serviceURL(serviceName, SERVICE_DOMAIN) : null
    if (!url) return
    window.location.replace(url)
  }, [serviceName])

  const handleRejected = useCallback(() => setStatus('rejected'), [])

  const { start } = useAccessRequestWatcher(serviceName, {
    onGranted: redirectToService,
    onRejected: handleRejected,
  })

  const handleRequest = useCallback(async () => {
    if (!serviceName) return
    setStatus('requesting')
    setErrorMessage('')

    const result = await requestAccess(serviceName)

    if (!result.ok) {
      setStatus('error')
      setErrorMessage(result.error ?? 'Something went wrong')
      return
    }

    // The watcher runs for a standing rejection too: the owner can accept after all, and this page is
    // where the requester is sitting when they do.
    setStatus(result.status === 'rejected' ? 'rejected' : 'sent')
    start()
  }, [serviceName, start])

  // Landing here means the router already turned this user away, and a request needs the owner's
  // approval to become anything — so ask on arrival rather than making them click to say what they
  // came here to say. The ref keeps StrictMode's second mount from asking twice; it holds the service
  // it asked for rather than a flag, because this component is reused when the route param changes and
  // a bare flag would swallow the request for the second service.
  const asked = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!serviceName) return
    let cancelled = false

    checkAccess(serviceName).then(result => {
      if (cancelled) return
      if (result?.authorized) {
        redirectToService()
        return
      }
      if (result?.serviceTitle) {
        setServiceTitle(result.serviceTitle)
      }
      if (asked.current === serviceName) return
      asked.current = serviceName
      handleRequest()
    })

    return () => {
      cancelled = true
    }
  }, [serviceName, redirectToService, handleRequest])

  return (
    <Stack
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        flexGrow: 1,
        minHeight: '95vh'
      }}>
      <Modal open inline maxWidth="xs" color="secondary.main">
        <DialogContent>
          <Stack
            sx={{
              gap: 3,
              py: 1
            }}>
            <Typography variant="subtitle1" color="secondary">
              Access Required
            </Typography>

            {!validName && (
              <Alert severity="error">This link does not name a valid service.</Alert>
            )}

            {validName && (
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>
                {email} cannot access <strong>{serviceTitle}</strong>.
              </Typography>
            )}

            {validName && status === 'requesting' && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                <CircularProgress color="inherit" size={18} />
                <Typography variant="body2">Requesting access…</Typography>
              </Stack>
            )}

            {status === 'sent' && (
              <Alert severity="info" icon={<LuCheck />}>
                Requested. This page will update when access is granted.
              </Alert>
            )}

            {status === 'rejected' && (
              <Alert severity="error" icon={<LuBan />}>
                Your request was rejected by the owner.
              </Alert>
            )}

            {status === 'error' && (
              <>
                <Alert severity="error">{errorMessage}</Alert>
                <Button size="large" variant="contained" color="secondary" onClick={handleRequest}>
                  Try Again
                </Button>
              </>
            )}
          </Stack>
        </DialogContent>
      </Modal>
    </Stack>
  )
}
